import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import type {
  BedrockCatalogModel,
  OrgModelConfigFilter,
} from "@/shared/domain/orgs";

/** The "Own models" filter's options, as `has_own_models` values. */
const OWN_MODELS_OPTIONS = [
  { value: "yes", label: "Has own models", hasOwnModels: true },
  { value: "no", label: "Roboto models only", hasOwnModels: false },
] as const;

const HAS_OWN_MODELS_VALUES = [true, false] as const;

/**
 * The filter menu's fields for the org model-configuration search. Each
 * maps onto one field of the search body (see {@link toModelConfigFilter});
 * the menu is limited to comparators that mapping can express.
 *
 * @param chatModels The catalog's chat models, the only ones an org can have.
 */
export function orgModelConfigFilterDefinitions(
  chatModels: readonly BedrockCatalogModel[],
): FilterDefinition[] {
  return [
    {
      type: "string",
      field: "name",
      name: "Org name or id",
      allowedComparators: ["CONTAINS"],
    },
    {
      type: "enum",
      field: "own_models",
      name: "Own models",
      options: OWN_MODELS_OPTIONS.map(({ value, label }) => ({ value, label })),
    },
    {
      type: "enum",
      field: "has_model",
      name: "Has model",
      options: chatModels.map((m) => ({ value: m.id, label: m.label })),
    },
  ];
}

/** The search body for a set of AND-ed filters, and what could not be applied. */
export interface ModelConfigFilterTranslation {
  filter: OrgModelConfigFilter;
  /** One sentence per filter (or part of one) the search cannot express. */
  unsupported: string[];
  /**
   * The "Own models" filters contradict each other, so no org can match.
   * The search body has no way to ask for no orgs, so the caller shows no
   * rows instead of searching.
   */
  matchesNothing: boolean;
}

/**
 * Translate the filter menu's structured filters into the search body.
 *
 * The search ANDs three fields: whether the org has own-connection models,
 * a name substring and one model id. Enum filters arrive as checklists, "is"
 * (`ANY_OF`) or "is not" (`NONE_OF`). "Own models" filters intersect (an
 * "is not" becomes the other option); allowing both options, like setting
 * none, leaves the field out. Only the first name search and the first
 * "has model" value apply; anything the body cannot say is reported rather
 * than dropped silently, so the page can tell the admin.
 */
export function toModelConfigFilter(
  filters: readonly Filter[],
): ModelConfigFilterTranslation {
  const unsupported: string[] = [];
  // The `has_own_models` values the "Own models" filters still allow.
  let allowedHasOwnModels: boolean[] = [...HAS_OWN_MODELS_VALUES];
  let nameQuery: string | undefined;
  let hasModel: { id: string; label: string } | undefined;

  for (const filter of filters) {
    if (filter.field === "own_models" && filter.type === "enum") {
      const picked = OWN_MODELS_OPTIONS.filter((option) =>
        filter.values.some((v) => v.value === option.value),
      ).map((option) => option.hasOwnModels);
      if (picked.length === 0) {
        continue;
      }
      const matching =
        filter.comparator === "NONE_OF"
          ? HAS_OWN_MODELS_VALUES.filter((value) => !picked.includes(value))
          : picked;
      allowedHasOwnModels = allowedHasOwnModels.filter((value) =>
        matching.includes(value),
      );
    } else if (filter.field === "name" && filter.type === "string") {
      const text = filter.values[0]?.trim() ?? "";
      if (nameQuery !== undefined) {
        unsupported.push(`Only one name search applies; "${text}" is ignored.`);
      } else if (text !== "") {
        nameQuery = text;
      }
    } else if (filter.field === "has_model" && filter.type === "enum") {
      if (filter.comparator !== "ANY_OF") {
        unsupported.push(
          'Has model supports only "is"; the "is not" filter is ignored.',
        );
        continue;
      }
      const [first] = filter.values;
      if (first === undefined) {
        continue;
      }
      if (hasModel !== undefined || filter.values.length > 1) {
        const kept = hasModel ?? { id: first.value, label: first.label };
        unsupported.push(
          `Has model matches one model at a time; only ${kept.label} is applied.`,
        );
      }
      hasModel ??= { id: first.value, label: first.label };
    }
  }

  return {
    filter: {
      ...(allowedHasOwnModels.length === 1 && {
        has_own_models: allowedHasOwnModels[0],
      }),
      ...(nameQuery !== undefined && { name_query: nameQuery }),
      ...(hasModel !== undefined && { has_model_id: hasModel.id }),
    },
    unsupported,
    matchesNothing: allowedHasOwnModels.length === 0,
  };
}
